import React, { useState, useCallback } from 'react';
import { Box, Typography, Badge, Paper, Button } from '@mui/material';
import { Droppable } from '@hello-pangea/dnd';
import KanbanCard from './KanbanCard';

const CARDS_PER_PAGE = 20;

const KanbanColumn = ({ stage, leads }) => {
  const [visibleCount, setVisibleCount] = useState(CARDS_PER_PAGE);

  const visibleLeads = leads.slice(0, visibleCount);
  const hasMore = leads.length > visibleCount;
  const remaining = leads.length - visibleCount;

  const handleShowMore = useCallback(() => {
    setVisibleCount((prev) => prev + CARDS_PER_PAGE);
  }, []);

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 220,
        flex: 1,
        bgcolor: '#F4F6F8',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        borderTop: `3px solid ${stage.color}`,
      }}
    >
      {/* Column Header */}
      <Box
        sx={{
          p: 1.5,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: stage.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {stage.label}
          </Typography>
        </Box>
        <Badge
          badgeContent={leads.length}
          color="default"
          max={9999}
          sx={{
            '& .MuiBadge-badge': {
              bgcolor: 'grey.300',
              color: 'text.secondary',
              fontSize: '0.7rem',
              fontWeight: 700,
              minWidth: 24,
              height: 20,
            },
          }}
        />
      </Box>

      {/* Droppable Card Area */}
      <Droppable droppableId={stage.id}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              flex: 1,
              p: 1,
              pt: 0.5,
              minHeight: 100,
              maxHeight: 'calc(100vh - 280px)',
              overflowY: 'auto',
              bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
              borderRadius: 1,
              transition: 'background-color 0.2s ease',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'grey.400',
                borderRadius: 2,
              },
            }}
          >
            {leads.length === 0 && !snapshot.isDraggingOver && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 80,
                }}
              >
                <Typography variant="body2" color="text.disabled" fontStyle="italic">
                  No leads
                </Typography>
              </Box>
            )}
            {visibleLeads.map((lead, index) => (
              <KanbanCard key={lead._id} lead={lead} index={index} />
            ))}
            {provided.placeholder}
            {/* Show more button */}
            {hasMore && (
              <Button
                size="small"
                fullWidth
                onClick={handleShowMore}
                sx={{
                  mt: 1,
                  textTransform: 'capitalize',
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  bgcolor: 'grey.200',
                  '&:hover': { bgcolor: 'grey.300' },
                }}
              >
                {`Show more (${remaining} remaining)`}
              </Button>
            )}
          </Box>
        )}
      </Droppable>
    </Paper>
  );
};

export default React.memo(KanbanColumn);
